import * as React from 'react'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { AppState } from '../../store'
import CardEmptyInfo from '../../components/Card/CardEmptyInfo'
import { showConfirmModal, showTourModal } from '../../store/modals/actions'
import { WidgetToursController } from '../../controllers'
import PageLoader from '../../components/Page/PageLoader'
import Badge from '../../components/Badge/Badge'
import { withTranslation, WithTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import ResourceTable, { ResourceTableAction } from '../../components/Resource/ResourceTable'
import ResourceTableRow from '../../components/Resource/ResourceTableRow'
import ResourceTableRowData from '../../components/Resource/ResourceTableRowData'
import ResourceTableRowActions from '../../components/Resource/ResourceTableRowActions'
import { CurrentUser, WidgetTour, WidgetTourFilter, ResourceListFilterType } from '../../types'
import moment from '../../utilities/Moment'
import Notification from '../../utilities/Notification'
import ButtonPanel from '../../components/Button/ButtonPanel'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'

interface IOwnProps {
  widgetId: string
}

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  showConfirmModal: typeof showConfirmModal
  showTourModal: typeof showTourModal
}

type IProps = IOwnProps & IStateToProps & IDispatchToProps & RouteComponentProps<any> & WithTranslation

interface IState {
  tours: WidgetTour[]
  page: number
  perPage: number
  totalPages: number
  didInitialLoad: boolean
  isFetching: boolean
  sortValue: string
  activeFilters: any
  selectedIds: string[]
  search: string
}

class WidgetTourTable extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)

    this.state = {
      tours: [],
      selectedIds: [],
      page: 1,
      perPage: 25,
      totalPages: 0,
      didInitialLoad: false,
      isFetching: false,
      sortValue: 'created_at_desc',
      activeFilters: {},
      search: '',
    }

    this.onTableActionClick = this.onTableActionClick.bind(this)
    this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
    this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
    this.onTableDeleteClick = this.onTableDeleteClick.bind(this)
    this.onNewTourClick = this.onNewTourClick.bind(this)
    this.onFiltersChange = this.onFiltersChange.bind(this)
    this.onSortValueChange = this.onSortValueChange.bind(this)
    this.onSearchChange = this.onSearchChange.bind(this)
    this.onSearchSubmit = this.onSearchSubmit.bind(this)
    this.onClearFilters = this.onClearFilters.bind(this)
    this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
    this.onRowClick = this.onRowClick.bind(this)
  }

  componentDidMount() {
    this.fetchTours(1)
  }

  fetchTours(page: number) {
    const { widgetId } = this.props
    const { search, sortValue, activeFilters, perPage } = this.state

    this.setState({ isFetching: true }, () => {
      WidgetToursController
        .getTours(widgetId, { page, per_page: perPage, search, order: sortValue, ...activeFilters })
        .then(response => {
          const { tours, current_page, total_pages } = response

          this.setState({
            tours: [...(tours || [])],
            page: current_page || page,
            totalPages: total_pages || 0,
            didInitialLoad: true,
            isFetching: false,
          })
        })
        .catch(console.error)
    })
  }

  onPageChange(page: number) {
    this.setState({ page }, () => this.fetchTours(page))
  }

  onPerPageChange(perPage: number) {
    this.setState({ page: 1, perPage }, () => this.fetchTours(1))
  }

  onTableActionClick(key: string, tour: WidgetTour) {
    switch (key) {
      case 'edit': this.onTableEditClick(tour)
        break
      case 'delete': this.onTableDeleteClick(tour)
        break
      default:
        throw Error('[WidgetTourTable] Unimplemented onTableActionClick')
    }
  }

  onTableEditClick(tour: WidgetTour) {
    const { widgetId, showTourModal } = this.props

    showTourModal({
      widgetId,
      tour,
      onSubmit: (updated) => {
        const { tours } = this.state
        const index = tours.findIndex(t => t.id === updated.id)
        if (index !== -1) {
          const next = [...tours]
          next[index] = updated
          this.setState({ tours: next })
        }
      },
    })
  }

  onTableSelectionChange(selectedIds: string[]) {
    this.setState({ selectedIds })
  }

  onTableRowSelectionChange(selected: boolean, id: string) {
    const { selectedIds } = this.state

    if (selected) {
      this.setState({ selectedIds: [...selectedIds, id] })
    } else {
      this.setState({ selectedIds: selectedIds.filter(selectedId => selectedId !== id) })
    }
  }

  onTableDeleteClick(tour: WidgetTour) {
    const { showConfirmModal, t, widgetId } = this.props

    showConfirmModal({
      title: t('WidgetTour::Delete tour'),
      description: t('WidgetTour::You are about to delete this tour. Are you sure?'),
      action: { label: t('WidgetTour::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await WidgetToursController.delete(widgetId, tour.id)

          if (response.errors) return

          const { tours, selectedIds } = this.state
          const index = tours.findIndex(c => c.id === tour.id)
          tours.splice(index, 1)

          this.setState({
            tours: [...tours],
            selectedIds: selectedIds.filter(id => id !== tour.id),
          })

          Notification.notifySuccess(t('WidgetTour::Tour successfully deleted'))
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters }, () => this.fetchTours(1))
  }

  onSortValueChange(value: string) {
    this.setState({ page: 1, sortValue: value }, () => this.fetchTours(1))
  }

  onSearchChange(search: string) {
    this.setState({ search })
  }

  onSearchSubmit(search: string) {
    this.setState({ page: 1, search }, () => this.fetchTours(1))
  }

  onClearFilters() {
    this.setState({ page: 1, search: '', activeFilters: {} }, () => this.fetchTours(1))
  }

  onBulkDeleteClick() {
    const { t, widgetId } = this.props
    const { selectedIds, tours } = this.state

    this.props.showConfirmModal({
      title: t('WidgetTour::Bulk delete tours'),
      description: t('WidgetTour::You are about to delete these tours. Are you sure?'),
      action: { label: t('WidgetTour::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await WidgetToursController.deleteAll(widgetId, selectedIds)

          if (response.errors) return

          const newTours = tours.filter(c => !selectedIds.includes(c.id))
          this.setState({ tours: [...newTours], selectedIds: [] })
          Notification.notifySuccess(t('WidgetTour::Tours successfully deleted'))
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  onNewTourClick() {
    const { widgetId, showTourModal } = this.props

    showTourModal({
      widgetId,
      onSubmit: (tour) => {
        const { tours } = this.state
        this.setState({ tours: [tour, ...tours] })
      },
    })
  }

  onRowClick(tour: WidgetTour) {
    this.props.history.push(RouteHelper.process(ERoute.PATH_WIDGET_TOUR, { id: this.props.widgetId, tourId: tour.id }))
  }

  render() {
    const { t } = this.props
    const {
      tours,
      selectedIds,
      didInitialLoad,
      isFetching,
      activeFilters,
      sortValue,
      search,
      page,
      perPage,
      totalPages,
    } = this.state

    const filtersActive = search?.length > 0 || Object.keys(activeFilters).length > 0
    const filters: WidgetTourFilter[] = [
      { name: 'name', label: t('WidgetTour::Name'), type: ResourceListFilterType.STRING },
      { name: 'created_at', label: t('WidgetTour::Created date'), type: ResourceListFilterType.DATE },
    ]
    const promotedBulkActions: ResourceTableAction[] = [
      { icon: 'trash-alt-solid', content: t('WidgetTour::Delete'), onAction: this.onBulkDeleteClick },
    ]

    if (!didInitialLoad) return <PageLoader />

    return (
      <ResourceTable
        data={tours}
        headers={[
          { title: t('WidgetTour::Name') },
          { title: t('WidgetTour::Status') },
          { title: t('WidgetTour::Created'), align: 'right' },
          { title: '', stickyRight: '0px' },
        ]}
        actionsLeft={[
          <ButtonPanel
            icon='plus'
            text={t('WidgetTour::New tour')}
            onClick={this.onNewTourClick}
          />
        ]}
        renderRow={(tour: WidgetTour) => (
          <ResourceTableRow
            key={tour.id}
            selected={selectedIds.includes(tour.id)}
            onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, tour.id)}
          >
            <ResourceTableRowData onClick={(): void => this.onRowClick(tour)}>
              {tour.name || t('WidgetTour::Untitled')}
            </ResourceTableRowData>
            <ResourceTableRowData onClick={(): void => this.onRowClick(tour)}>
              <Badge type={tour.enabled ? 'success' : 'grey'} text={tour.enabled ? t('WidgetTour::Enabled') : t('WidgetTour::Disabled')} />
            </ResourceTableRowData>
            <ResourceTableRowData textAlign='right' onClick={(): void => this.onRowClick(tour)}>
              {tour.created_at ? moment(tour.created_at).format('LL') : '-'}
            </ResourceTableRowData>
            <ResourceTableRowActions
              actions={[
                { key: 'edit', icon: 'edit', content: t('WidgetTour::Edit') },
                { key: 'delete', icon: 'trash-alt-solid', content: t('WidgetTour::Delete'), destructive: true },
              ]}
              onActionClick={(key) => this.onTableActionClick(key, tour)}
              sticky={true}
              stickyRight='0px'
            />
          </ResourceTableRow>
        )}
        renderEmpty={<CardEmptyInfo
          icon={filtersActive ? 'search' : 'send'}
          description={filtersActive ? t('WidgetTour::No tours found') : t('WidgetTour::No tours have been added yet')}
          descriptionActionText={filtersActive ? t('WidgetTour::Clear filters') : undefined}
          onDescriptionActionClick={filtersActive ? this.onClearFilters : undefined}
        />}
        filters={filters}
        activeFilters={activeFilters}
        onFiltersChange={this.onFiltersChange}
        sortOptions={[
          { label: '-', value: '-' },
          { label: t('WidgetTour::Name (A-Z)'), value: 'name_asc' },
          { label: t('WidgetTour::Name (Z-A)'), value: 'name_desc' },
          { label: t('WidgetTour::Created at ↑'), value: 'created_at_asc' },
          { label: t('WidgetTour::Created at ↓'), value: 'created_at_desc' },
        ]}
        sortValue={sortValue}
        onSortChange={this.onSortValueChange}
        pagination={{ page, perPage, pageCount: totalPages }}
        onPageChange={this.onPageChange}
        onPerPageChange={this.onPerPageChange}
        isLoading={isFetching}
        stickyHeader={true}
        selectedItems={selectedIds}
        onSelectionChange={this.onTableSelectionChange}
        promotedBulkActions={promotedBulkActions}
        searchValue={search}
        onSearchChange={this.onSearchChange}
        onSearchSubmit={this.onSearchSubmit}
      />
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const { authentication: { currentUser } } = state
  return { currentUser }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => ({
  showConfirmModal: (options) => dispatch(showConfirmModal(options)),
  showTourModal: (options) => dispatch(showTourModal(options)),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(WidgetTourTable)) as any) as any
